// apps/web/src/components/astral/useCanvasTouchGestures.ts
import { useEffect, useRef } from "react";
import type { Dispatch, RefObject, SetStateAction } from "react";

interface UseCanvasTouchGesturesProps {
  containerRef: RefObject<HTMLDivElement | null>;
  pan: { x: number; y: number };
  zoom: number;
  setPan: Dispatch<SetStateAction<{ x: number; y: number }>>;
  setZoom: Dispatch<SetStateAction<number>>;
  onViewportChangeEnd?: (pan: { x: number; y: number }, zoom: number) => void;
  onDoubleTap?: () => void;
  readOnly?: boolean;
  disabled?: boolean;
}

export function useCanvasTouchGestures({
  containerRef,
  pan,
  zoom,
  setPan,
  setZoom,
  onViewportChangeEnd,
  onDoubleTap,
  readOnly = false,
  disabled = false,
}: UseCanvasTouchGesturesProps) {
  const panRef = useRef(pan);
  panRef.current = pan;

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  // Estados de rastreamento de toque
  const isTouchPanningRef = useRef(false);
  const isPinchingRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const touchStartPosRef = useRef({ x: 0, y: 0 });
  const lastTouchPosRef = useRef({ x: 0, y: 0 });
  const lastTouchTimeRef = useRef(0);
  const velocityRef = useRef({ vx: 0, vy: 0 });
  const momentumRafRef = useRef<number | null>(null);

  // Pinch-to-zoom referências
  const pinchStartDistRef = useRef(0);
  const pinchStartZoomRef = useRef(1);
  const pinchMidpointRef = useRef({ x: 0, y: 0 });
  const pinchStartPanRef = useRef({ x: 0, y: 0 });

  // Detecção de Double Tap
  const lastTapTimeRef = useRef(0);
  const lastTapPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el || disabled) return;

    const stopMomentum = () => {
      if (momentumRafRef.current) {
        cancelAnimationFrame(momentumRafRef.current);
        momentumRafRef.current = null;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement | null;

      // Se tocar em botões de zoom, inputs ou links, não interceptar
      if (
        target?.closest("button") ||
        target?.closest("input") ||
        target?.closest("a") ||
        target?.closest(".astral-floating-ctrl")
      ) {
        return;
      }

      // Se não for readOnly e o toque for em um card de nó, o editor cuida do drag do nó
      if (!readOnly && target?.closest("[data-astral-node]")) {
        return;
      }

      stopMomentum();

      if (e.touches.length === 1) {
        const t = e.touches[0]!;
        const now = performance.now();

        // 1. Detecção de Double Tap para auto-fit
        const timeSinceLast = now - lastTapTimeRef.current;
        const distSinceLast = Math.hypot(
          t.clientX - lastTapPosRef.current.x,
          t.clientY - lastTapPosRef.current.y,
        );

        if (timeSinceLast < 320 && distSinceLast < 24) {
          onDoubleTap?.();
          lastTapTimeRef.current = 0;
          isTouchPanningRef.current = false;
          return;
        }

        lastTapTimeRef.current = now;
        lastTapPosRef.current = { x: t.clientX, y: t.clientY };

        // Inicia Pan com 1 dedo
        isTouchPanningRef.current = true;
        isPinchingRef.current = false;
        panStartRef.current = { x: panRef.current.x, y: panRef.current.y };
        touchStartPosRef.current = { x: t.clientX, y: t.clientY };
        lastTouchPosRef.current = { x: t.clientX, y: t.clientY };
        lastTouchTimeRef.current = now;
        velocityRef.current = { vx: 0, vy: 0 };
      } else if (e.touches.length === 2) {
        // Inicia Pinch-to-zoom com 2 dedos
        isTouchPanningRef.current = false;
        isPinchingRef.current = true;

        const t1 = e.touches[0]!;
        const t2 = e.touches[1]!;
        pinchStartDistRef.current = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        pinchStartZoomRef.current = zoomRef.current;

        const rect = el.getBoundingClientRect();
        pinchMidpointRef.current = {
          x: (t1.clientX + t2.clientX) / 2 - rect.left,
          y: (t1.clientY + t2.clientY) / 2 - rect.top,
        };
        pinchStartPanRef.current = { x: panRef.current.x, y: panRef.current.y };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isPinchingRef.current && e.touches.length === 2) {
        if (e.cancelable) e.preventDefault();

        const t1 = e.touches[0]!;
        const t2 = e.touches[1]!;
        const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

        if (pinchStartDistRef.current > 0) {
          const ratio = dist / pinchStartDistRef.current;
          const newZoom = Math.min(2.5, Math.max(0.15, pinchStartZoomRef.current * ratio));

          // Ajusta pan para que o ponto central dos dedos permaneça fixo no canvas
          const mx = pinchMidpointRef.current.x;
          const my = pinchMidpointRef.current.y;
          const oldZ = pinchStartZoomRef.current;

          const newPanX = mx - (mx - pinchStartPanRef.current.x) * (newZoom / oldZ);
          const newPanY = my - (my - pinchStartPanRef.current.y) * (newZoom / oldZ);

          setZoom(newZoom);
          setPan({ x: Math.round(newPanX), y: Math.round(newPanY) });
        }
      } else if (isTouchPanningRef.current && e.touches.length === 1) {
        if (e.cancelable) e.preventDefault();

        const t = e.touches[0]!;
        const dx = t.clientX - touchStartPosRef.current.x;
        const dy = t.clientY - touchStartPosRef.current.y;

        const newPanX = panStartRef.current.x + dx;
        const newPanY = panStartRef.current.y + dy;

        setPan({ x: newPanX, y: newPanY });

        // Calcula velocidade para momentum
        const now = performance.now();
        const dt = Math.max(1, now - lastTouchTimeRef.current);
        velocityRef.current = {
          vx: (t.clientX - lastTouchPosRef.current.x) / dt,
          vy: (t.clientY - lastTouchPosRef.current.y) / dt,
        };

        lastTouchPosRef.current = { x: t.clientX, y: t.clientY };
        lastTouchTimeRef.current = now;
      }
    };

    const handleTouchEnd = () => {
      if (isPinchingRef.current) {
        isPinchingRef.current = false;
        onViewportChangeEnd?.(panRef.current, zoomRef.current);
        return;
      }

      if (isTouchPanningRef.current) {
        isTouchPanningRef.current = false;

        // Inércia / Momentum suave
        let { vx, vy } = velocityRef.current;
        const speed = Math.hypot(vx, vy);

        if (speed > 0.15) {
          const friction = 0.92;
          let currentPanX = panRef.current.x;
          let currentPanY = panRef.current.y;

          const step = () => {
            vx *= friction;
            vy *= friction;

            if (Math.hypot(vx, vy) < 0.02) {
              onViewportChangeEnd?.(panRef.current, zoomRef.current);
              return;
            }

            currentPanX += vx * 16;
            currentPanY += vy * 16;

            setPan({ x: Math.round(currentPanX), y: Math.round(currentPanY) });
            momentumRafRef.current = requestAnimationFrame(step);
          };

          momentumRafRef.current = requestAnimationFrame(step);
        } else {
          onViewportChangeEnd?.(panRef.current, zoomRef.current);
        }
      }
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);
    el.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      stopMomentum();
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [containerRef, disabled, onDoubleTap, onViewportChangeEnd, readOnly, setPan, setZoom]);
}
